import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/shadcn/utils';
import { Button } from '@/modules/ui/components/button/Button';

interface PortalEmptyStateAction {
  label: string;
  /** Where the action goes. Omit and pass `onClick` for an in-page action. */
  to?: string;
  /** Leaves the app (mail, another site) in a new tab. `panel` only. */
  href?: string;
  onClick?: () => void;
}

interface PortalEmptyStateProps {
  icon: LucideIcon;
  title: string;
  description?: ReactNode;
  /** The one thing to do next, as the primary button (`panel`) or a trailing link (`row`). */
  action?: PortalEmptyStateAction;
  /** A quieter alternative next to the primary action. `panel` only. */
  secondaryAction?: PortalEmptyStateAction;
  /**
   * `panel`: the whole page has nothing yet, so the state earns a headline and
   * a button. `row`: a section of an otherwise populated page is empty, so it
   * stays a quiet line between the list's hairlines.
   */
  size?: 'panel' | 'row';
  /** Pulsing dot: Finlay is working on this right now, nothing for the user to do. */
  live?: boolean;
  className?: string;
}

/**
 * The portal's empty state. Replaces the hairline note each page used to
 * hand-roll, and gives the page-level cases a title and a next step so an
 * empty portal is never a dead end.
 */
export function PortalEmptyState({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  size = 'panel',
  live = false,
  className,
}: PortalEmptyStateProps) {
  if (size === 'row') {
    return (
      <div
        className={cn(
          'text-muted-foreground flex flex-wrap items-start gap-x-3 gap-y-1 border-y px-2 py-5 text-[13px]',
          className,
        )}
      >
        {live ? (
          <LiveDot className="mt-1.5" />
        ) : (
          <Icon
            aria-hidden
            className="mt-0.5 size-4 shrink-0 text-neutral-500"
          />
        )}
        <span className="min-w-0 flex-1">
          <span className="text-foreground font-medium">{title}</span>
          {description && <> {description}</>}
        </span>
        {action &&
          (action.to ? (
            <Link
              className="text-primary font-medium whitespace-nowrap hover:underline"
              to={action.to}
            >
              {action.label}
            </Link>
          ) : (
            <button
              className="text-primary cursor-pointer font-medium whitespace-nowrap hover:underline"
              onClick={action.onClick}
              type="button"
            >
              {action.label}
            </button>
          ))}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'bg-muted/30 flex flex-col items-center gap-5 rounded-xl border px-6 py-8 text-center',
        className,
      )}
    >
      <span className="bg-muted relative grid size-11 place-items-center rounded-xl text-neutral-600">
        <Icon aria-hidden className="size-5" />
        {live && <LiveDot className="absolute -top-1 -right-1" />}
      </span>
      <div className="flex max-w-md flex-col gap-1.5">
        <h3 className="text-[15px] leading-6 font-semibold tracking-[-0.01em]">
          {title}
        </h3>
        {description && (
          <p className="text-muted-foreground text-[13px] leading-5">
            {description}
          </p>
        )}
      </div>
      {(action || secondaryAction) && (
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
          {action &&
            (action.to ? (
              <Button asChild>
                <Link to={action.to}>{action.label}</Link>
              </Button>
            ) : action.href ? (
              <Button asChild>
                <a href={action.href} rel="noopener noreferrer" target="_blank">
                  {action.label}
                </a>
              </Button>
            ) : (
              <Button onClick={action.onClick}>{action.label}</Button>
            ))}
          {secondaryAction?.to && (
            <Button asChild size="sm" variant="outline">
              <Link to={secondaryAction.to}>{secondaryAction.label}</Link>
            </Button>
          )}
          {secondaryAction?.href && (
            <Button asChild size="sm" variant="outline">
              <a
                href={secondaryAction.href}
                rel="noopener noreferrer"
                target="_blank"
              >
                {secondaryAction.label}
              </a>
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/** Finlay is working: the same pulse the job rows use for a live search. */
function LiveDot({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        'bg-semantic-560 block size-2 shrink-0 animate-[portal-live_1.6s_ease-in-out_infinite] rounded-full ring-2 ring-white motion-reduce:animate-none',
        className,
      )}
    />
  );
}
